import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Eye, Sparkles, Save, Trash2, History } from 'lucide-react';
import { getErrorMessage } from '../../utils/errorHandler';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BrandingSettings({
  project,
  setProject,
  onOpenPreview,
  onOpenGallery,
  t,
}) {
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);

  const fetchPresets = async () => {
    setPresetsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/branding/presets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPresets(res.data?.presets || []);
    } catch (error) {
      // Silent fail - presets are non-critical
      console.error('Failed to load branding presets', error);
    } finally {
      setPresetsLoading(false);
    }
  };

  useEffect(() => {
    fetchPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSavePreset = async () => {
    if (!project.branding_logo_url && !project.branding_text) {
      toast.error(t('preset_needs_logo_or_text') || 'Add a logo URL or branding text before saving as a preset');
      return;
    }
    setSavingPreset(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        branding_logo_url: project.branding_logo_url || null,
        branding_logo_position: project.branding_logo_position || 'bottom-right',
        branding_logo_size: project.branding_logo_size || 'small',
        branding_logo_opacity: project.branding_logo_opacity ?? 0.8,
        branding_text: project.branding_text || null,
        branding_text_position: project.branding_text_position || 'bottom-center',
      };
      const res = await axios.post(`${API}/api/branding/presets`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.updated ? (t('preset_updated') || 'Preset refreshed') : (t('preset_saved') || 'Branding preset saved'));
      fetchPresets();
    } catch (error) {
      toast.error(getErrorMessage(error, t('preset_save_failed') || 'Failed to save preset'));
    } finally {
      setSavingPreset(false);
    }
  };

  const handleApplyPreset = (preset) => {
    setProject({
      ...project,
      branding_enabled: true,
      branding_logo_url: preset.branding_logo_url || '',
      branding_logo_position: preset.branding_logo_position || 'bottom-right',
      branding_logo_size: preset.branding_logo_size || 'small',
      branding_logo_opacity: preset.branding_logo_opacity ?? 0.8,
      branding_text: preset.branding_text || '',
      branding_text_position: preset.branding_text_position || 'bottom-center',
    });
    toast.success(`${t('applied') || 'Applied'}: ${preset.name || t('preset') || 'Preset'}`);
  };

  const handleDeletePreset = async (presetId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/branding/presets/${presetId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('preset_deleted') || 'Preset removed');
      fetchPresets();
    } catch (error) {
      toast.error(getErrorMessage(error, t('preset_delete_failed') || 'Failed to delete preset'));
    }
  };

  return (
    <div className="space-y-4 pt-4 border-t" data-testid="branding-settings">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('custom_branding') || 'Custom Branding'}</Label>
        <Badge variant="outline" className="text-xs">Pro+</Badge>
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-sm">{t('enable_branding') || 'Enable Branding'}</Label>
        <Switch
          checked={project.branding_enabled || false}
          onCheckedChange={(checked) => setProject({ ...project, branding_enabled: checked })}
          data-testid="branding-enabled-switch"
        />
      </div>

      {/* Browse Templates Button */}
      <Button
        variant="outline"
        className="w-full gap-2"
        onClick={onOpenGallery}
        data-testid="browse-templates-btn"
      >
        <Sparkles className="w-4 h-4" />
        {t('browse_templates') || 'Browse Templates'}
      </Button>

      {/* Recent Branding Presets */}
      {presets.length > 0 && (
        <div className="space-y-2" data-testid="branding-presets-section">
          <Label className="text-xs text-muted-foreground flex items-center gap-1">
            <History className="w-3.5 h-3.5" />
            {t('recent_branding') || 'Recent Branding'}
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {presets.map((preset) => (
              <button
                key={preset.preset_id}
                type="button"
                onClick={() => handleApplyPreset(preset)}
                className="group relative flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-xs transition-colors max-w-[160px]"
                data-testid={`apply-preset-${preset.preset_id}`}
                title={t('click_to_apply') || 'Click to apply'}
              >
                {preset.branding_logo_url && (
                  <img
                    src={preset.branding_logo_url}
                    alt=""
                    className="w-4 h-4 rounded object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <span className="truncate">{preset.name || t('preset') || 'Preset'}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => handleDeletePreset(preset.preset_id, e)}
                  className="ml-0.5 opacity-50 hover:opacity-100 hover:text-red-500 transition-opacity cursor-pointer"
                  aria-label="Delete preset"
                  data-testid={`delete-preset-${preset.preset_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {project.branding_enabled && (
        <div className="space-y-4 pl-4 border-l-2 border-amber-500/30">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">{t('logo_url') || 'Logo URL'}</Label>
            <Input
              placeholder="https://example.com/logo.png"
              value={project.branding_logo_url || ''}
              onChange={(e) => setProject({ ...project, branding_logo_url: e.target.value })}
              data-testid="branding-logo-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('logo_position') || 'Logo Position'}</Label>
              <Select
                value={project.branding_logo_position || 'bottom-right'}
                onValueChange={(value) => setProject({ ...project, branding_logo_position: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-left">{t('top_left') || 'Top Left'}</SelectItem>
                  <SelectItem value="top-right">{t('top_right') || 'Top Right'}</SelectItem>
                  <SelectItem value="bottom-left">{t('bottom_left') || 'Bottom Left'}</SelectItem>
                  <SelectItem value="bottom-right">{t('bottom_right') || 'Bottom Right'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('logo_size') || 'Logo Size'}</Label>
              <Select
                value={project.branding_logo_size || 'small'}
                onValueChange={(value) => setProject({ ...project, branding_logo_size: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">{t('small') || 'Small'}</SelectItem>
                  <SelectItem value="medium">{t('medium') || 'Medium'}</SelectItem>
                  <SelectItem value="large">{t('large') || 'Large'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              {t('logo_opacity') || 'Logo Opacity'}: {Math.round((project.branding_logo_opacity || 0.8) * 100)}%
            </Label>
            <Slider
              value={[project.branding_logo_opacity || 0.8]}
              min={0.2}
              max={1}
              step={0.1}
              onValueChange={([value]) => setProject({ ...project, branding_logo_opacity: value })}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">{t('branding_text') || 'Branding Text'} ({t('optional') || 'optional'})</Label>
            <Input
              placeholder={t('branding_text_placeholder') || 'e.g., Your Church Name'}
              value={project.branding_text || ''}
              onChange={(e) => setProject({ ...project, branding_text: e.target.value })}
            />
          </div>

          {/* Action Buttons: Preview + Save Preset */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenPreview}
              data-testid="branding-preview-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              {t('preview') || 'Preview'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSavePreset}
              disabled={savingPreset || (!project.branding_logo_url && !project.branding_text)}
              data-testid="save-preset-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingPreset ? (t('saving') || 'Saving...') : (t('save_preset') || 'Save Preset')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
